import {
  type PropPlacement,
  ringDistance,
  silhouetteOf,
  STATION_REACH,
  type StationKind,
  type StationRejected,
  type StationState,
  type TilePosition,
} from '@shefira/shared';

/**
 * The Company's posts round Shefira's arena (ADR 0068 §5), as the client
 * finds them under a click and walks to them: each stands where its prop
 * does, clickable over the prop's whole silhouette; one walks to the open
 * tile within reach nearest oneself, and asks there. The server judges the
 * reach and the cooldown again.
 */

/** A post's silhouette on the map: its prop's footprint and the rows it rises above it. */
export interface StationBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export function stationBox(station: TilePosition, props: readonly PropPlacement[]): StationBox {
  const placed = props.find((prop) => prop.x === station.x && prop.y === station.y);
  if (placed === undefined) {
    return { x0: station.x, x1: station.x, y0: station.y - 1, y1: station.y };
  }
  const { x0, x1, y0, y1 } = silhouetteOf(placed);
  return { x0, x1, y0, y1 };
}

/** The post a click on this tile means, if any. */
export function stationAt(
  stations: readonly StationState[],
  props: readonly PropPlacement[],
  tile: TilePosition,
): StationState | undefined {
  return stations.find((station) => {
    const box = stationBox(station, props);
    return tile.x >= box.x0 && tile.x <= box.x1 && tile.y >= box.y0 && tile.y <= box.y1;
  });
}

/** Whether a player standing here may use the post, as the server judges it. */
export function inStationReach(station: TilePosition, at: TilePosition): boolean {
  return ringDistance(station, at) <= STATION_REACH;
}

/**
 * Where to walk to use a post: the open tile within its reach nearest the
 * player (the player's own tile when already there), the one in front of it
 * first on a tie. Undefined when rock surrounds it.
 */
export function stationApproach(
  station: TilePosition,
  isOpen: (tile: TilePosition) => boolean,
  from: TilePosition,
): TilePosition | undefined {
  if (inStationReach(station, from)) return { ...from };
  let best: TilePosition | undefined;
  let bestScore = Infinity;
  for (let dy = -STATION_REACH; dy <= STATION_REACH; dy += 1) {
    for (let dx = -STATION_REACH; dx <= STATION_REACH; dx += 1) {
      const tile = { x: station.x + dx, y: station.y + dy };
      if (!isOpen(tile)) continue;
      // Chebyshev to the player, then the tile before the post (south) on a tie.
      const score = ringDistance(tile, from) * 100 + Math.abs(dx) + Math.abs(dy - 1);
      if (score < bestScore) {
        best = tile;
        bestScore = score;
      }
    }
  }
  return best;
}

/** Each post's name, as the chat and its tooltip say it. */
export const STATION_NAMES: Readonly<Record<StationKind, string>> = {
  ballista: 'Baliste de Nella',
  'frost-brazier': 'Brasero de givre d’Ulfar',
  horn: 'Cor de Maëlle',
};

/** Why a post said no, in plain words. */
export const STATION_REFUSALS: Readonly<Record<StationRejected['reason'], string>> = {
  invalid: 'Demande invalide.',
  unknown: 'Ce poste n’existe pas.',
  'too-far': 'Approche-toi du poste.',
  cooldown: 'Ce poste n’est pas encore prêt.',
  'not-now': 'Pas maintenant : ce poste ne sert à rien pour l’instant.',
  dead: 'Pas tant que tu es à terre.',
};

/** Seconds left before a post is ready again, rounded up; 0 when it is. */
export function secondsLeft(readyAt: number, now: number): number {
  return Math.max(0, Math.ceil((readyAt - now) / 1000));
}
